import React, { useEffect, useState } from "react";
import { db } from "@/config/firebase";
import { collection, query, where, onSnapshot, doc } from "firebase/firestore";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Sparkles, Trophy, Wallet, TrendingUp } from "lucide-react";
import { Leaderboard } from "@/components/economy/Leaderboard";
import { Portfolio } from "@/components/economy/Portfolio";
import { CoinDisplay } from "@/components/economy/OddsBadge";
import { PredictionMarket } from "@/components/economy/PredictionMarket";

const CURRENT_SEASON = "2026-27";

export default function PredictionsHub() {
  const { user, role } = useAuth();
  const navigate = useNavigate();

  const [wallet, setWallet] = useState(null);
  const [activeSessions, setActiveSessions] = useState([]);
  const [activeTab, setActiveTab] = useState('market'); // 'market', 'portfolio', 'leaderboard'

  // Subscribe to wallet
  useEffect(() => {
    if (!user) return;
    const walletRef = doc(db, "prediction_wallets", user.uid);
    const unsub = onSnapshot(walletRef, (snap) => {
      if (snap.exists()) setWallet(snap.data());
    });
    return () => unsub();
  }, [user]);

  const pickTab = (t) => setActiveTab(t);

  // Subscribe to active sessions (only used to surface a live-game banner)
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, "prediction_sessions"), where("status", "==", "active")),
      (snap) => setActiveSessions(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return () => unsub();
  }, []);

  // A session counts as "live now" only while it is still in a playable phase
  // AND not stale/abandoned (a running game advances its phase every ~2 min,
  // so a phaseEndsAt that lies far in the past means nobody is hosting it).
  const SETTLED_PHASES = ['CHALLENGE_SETTLED', 'SETTLED', 'ENDED', 'FINISHED', 'DONE', 'SESSION_COMPLETE'];
  const STALE_MS = 2 * 60 * 60 * 1000; // 2 Stunden
  const isStale = (s) => {
    const raw = s.phaseEndsAt?.toDate?.() || (s.phaseEndsAt ? new Date(s.phaseEndsAt) : null);
    if (!raw) return false;
    return (Date.now() - raw.getTime()) > STALE_MS;
  };
  const liveSessions = activeSessions.filter(s => !SETTLED_PHASES.includes(s.phase) && !isStale(s));

  return (
    <div className="min-h-screen bg-[#0a0608] text-rose-50 pb-24">
      {/* Header */}
      <header className="px-4 py-3 bg-gradient-to-r from-rose-900/40 to-purple-900/40 border-b border-white/5 sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/')} className="p-1">
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1">
            <h1 className="font-semibold flex items-center gap-2">
              <Sparkles size={18} className="text-amber-400" />
              Season Predictions
            </h1>
            <p className="text-xs text-rose-200/50">2026/27</p>
          </div>
          {/* Wallet Quick View — Wett-Coins (verwettbar) + gesperrte Gewinne */}
          {wallet && (
            <div className="flex items-center gap-2 bg-black/30 px-3 py-1.5 rounded-full">
              <Wallet size={14} className="text-yellow-400" />
              <CoinDisplay amount={(wallet.balance || 0) - (wallet.reserved || 0)} size="sm" />
              {(wallet.lockedBalance || 0) > 0 && (
                <span className="text-[11px] text-purple-300/80" title="gesperrte Gewinne">🔒{wallet.lockedBalance}</span>
              )}
            </div>
          )}
          {role === 'admin' && (
            <Button
              onClick={() => navigate('/admin/predictions')}
              size="sm"
              className="bg-white/10 hover:bg-white/20"
            >
              Admin
            </Button>
          )}
        </div>
      </header>

      {/* Tab Navigation — Fokus: Wetten & Ranking */}
      <div className="sticky top-[60px] z-10 px-4 py-2 bg-[#0a0608]/95 backdrop-blur-sm border-b border-white/5">
        <div className="flex gap-1 bg-black/30 rounded-lg p-1">
          <button
            onClick={() => pickTab('market')}
            data-testid="pred-tab-market"
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'market'
                ? "bg-orange-500/30 text-orange-300"
                : "text-white/60 hover:text-white"
            }`}
          >
            <TrendingUp size={16} />
            Wetten
          </button>
          <button
            onClick={() => pickTab('portfolio')}
            data-testid="pred-tab-portfolio"
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'portfolio'
                ? "bg-yellow-500/30 text-yellow-300"
                : "text-white/60 hover:text-white"
            }`}
          >
            <Wallet size={16} />
            Meine Wetten
          </button>
          <button
            onClick={() => pickTab('leaderboard')}
            data-testid="pred-tab-ranking"
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'leaderboard'
                ? "bg-purple-500/30 text-purple-300"
                : "text-white/60 hover:text-white"
            }`}
          >
            <Trophy size={16} />
            Ranking
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="px-4 py-4">
        {/* Live-Spiel Banner (nur wenn wirklich ein Spiel läuft) */}
        {liveSessions.length > 0 && (
          <button
            onClick={() => navigate(`/predictions/live/${liveSessions[0].id}`)}
            data-testid="pred-live-banner"
            className="w-full mb-4 flex items-center justify-between gap-3 rounded-xl px-4 py-3 bg-green-600/20 border border-green-500/40 hover:bg-green-600/30 transition-colors"
          >
            <span className="flex items-center gap-2 text-green-300 font-semibold">
              <span className="w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse" />
              Live-Spiel läuft: {liveSessions[0].name}
            </span>
            <span className="text-sm text-green-200">Teilnehmen →</span>
          </button>
        )}

        {activeTab === 'market' && (
          <div className="space-y-4">
            <PredictionMarket seasonId={CURRENT_SEASON} wallet={wallet} />
          </div>
        )}

        {activeTab === 'portfolio' && (
          <Portfolio />
        )}

        {activeTab === 'leaderboard' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Trophy className="w-6 h-6 text-yellow-400" />
              <h2 className="text-xl font-bold">Ranking</h2>
            </div>
            <Leaderboard
              maxEntries={30}
              showDetails={true}
              compact={false}
              adminMode={role === 'admin'}
            />
            {role === 'admin' && (
              <p className="text-xs text-white/40 mt-3 text-center">
                Admin: mit −/+ (je 10) oder ✎ (beliebig) kannst du Coins vergeben/abziehen.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
